/**
 * NOTE (Yuxin, 2026-01-31): useSandboxFileActions hook
 *
 * Provides file mutation operations for the sandbox filesystem.
 * Uses the agent connection from useReploAgent to avoid multiple connections.
 *
 * All mutations use optimistic updates - the UI updates immediately without
 * waiting for git/R2 sync to complete. If the operation fails, the cache is
 * rolled back to the previous state.
 */

import type {
  ReploAgent,
  SandboxFile,
} from "@agent-ui/features/chat/hooks/useReploAgentConnection";

import { toast } from "@replo/design-system/components/toast/Toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// =============================================================================
// Helpers
// =============================================================================

function deriveFilepathFromRoute(route: string): string {
  const dir = route === "/" ? "app" : `app${route}`;
  return `${dir}/page.tsx`;
}

// =============================================================================
// Hook
// =============================================================================

export function useSandboxFileActions({
  agent,
  projectId,
}: {
  agent: ReploAgent;
  projectId: string | null;
}) {
  const queryClient = useQueryClient();
  const queryKey = ["sandboxFiles", projectId];

  const createFile = useMutation({
    mutationFn: async ({ route }: { route: string }) => {
      if (!agent) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("Agent not connected");
      }
      if (!projectId) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("No project ID");
      }
      return (await agent.call("createFile", [{ route }])) as { ok: boolean };
    },
    onMutate: async ({ route }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousFiles = queryClient.getQueryData<SandboxFile[]>(queryKey);

      // Optimistically add the new file
      queryClient.setQueryData<SandboxFile[]>(queryKey, (old) => [
        ...(old ?? []),
        {
          route,
          filepath: deriveFilepathFromRoute(route),
        },
      ]);

      return { previousFiles };
    },
    onSuccess: (_data, variables) => {
      toast.success({
        title: "Page created",
        description: `Created page at ${variables.route}`,
      });
    },
    onError: (error, _variables, context) => {
      // Roll back to the previous state
      if (context?.previousFiles) {
        queryClient.setQueryData(queryKey, context.previousFiles);
      }
      toast.error({
        title: "Failed to create page",
        description: String(error),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const renameFile = useMutation({
    mutationFn: async ({
      fromRoute,
      toRoute,
    }: {
      fromRoute: string;
      toRoute: string;
    }) => {
      if (!agent) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("Agent not connected");
      }
      if (!projectId) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("No project ID");
      }
      return (await agent.call("renameFile", [{ fromRoute, toRoute }])) as {
        ok: boolean;
      };
    },
    onMutate: async ({ fromRoute, toRoute }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousFiles = queryClient.getQueryData<SandboxFile[]>(queryKey);

      queryClient.setQueryData<SandboxFile[]>(queryKey, (old) => {
        return (old ?? []).map((file) => {
          return file.route === fromRoute
            ? {
                ...file,
                route: toRoute,
                filepath: deriveFilepathFromRoute(toRoute),
              }
            : file;
        });
      });

      return { previousFiles };
    },
    onSuccess: (_data, variables) => {
      toast.success({
        title: "Page renamed",
        description: `Renamed to ${variables.toRoute}`,
      });
    },
    onError: (error, _variables, context) => {
      if (context?.previousFiles) {
        queryClient.setQueryData(queryKey, context.previousFiles);
      }
      toast.error({
        title: "Failed to rename page",
        description: String(error),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const duplicateFile = useMutation({
    mutationFn: async ({
      route,
      newRoute,
    }: {
      route: string;
      newRoute: string;
    }) => {
      if (!agent) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("Agent not connected");
      }
      if (!projectId) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("No project ID");
      }
      return (await agent.call("duplicateFile", [{ route, newRoute }])) as {
        ok: boolean;
      };
    },
    onMutate: async ({ newRoute }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousFiles = queryClient.getQueryData<SandboxFile[]>(queryKey);

      // Optimistically add the duplicated file
      queryClient.setQueryData<SandboxFile[]>(queryKey, (old) => [
        ...(old ?? []),
        {
          route: newRoute,
          filepath: deriveFilepathFromRoute(newRoute),
        },
      ]);

      return { previousFiles };
    },
    onSuccess: (_data, variables) => {
      toast.success({
        title: "Page duplicated",
        description: `Created copy at ${variables.newRoute}`,
      });
    },
    onError: (error, _variables, context) => {
      if (context?.previousFiles) {
        queryClient.setQueryData(queryKey, context.previousFiles);
      }
      toast.error({
        title: "Failed to duplicate page",
        description: String(error),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteFile = useMutation({
    mutationFn: async ({ route }: { route: string }) => {
      if (!agent) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("Agent not connected");
      }
      if (!projectId) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("No project ID");
      }
      return (await agent.call("deleteFile", [{ route }])) as { ok: boolean };
    },
    onMutate: async ({ route }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousFiles = queryClient.getQueryData<SandboxFile[]>(queryKey);

      // Optimistically remove the deleted file
      queryClient.setQueryData<SandboxFile[]>(queryKey, (old) => {
        return (old ?? []).filter((file) => file.route !== route);
      });

      return { previousFiles };
    },
    onSuccess: (_data, variables) => {
      toast.success({
        title: "Page deleted",
        description: `Deleted ${variables.route}`,
      });
    },
    onError: (error, _variables, context) => {
      if (context?.previousFiles) {
        queryClient.setQueryData(queryKey, context.previousFiles);
      }
      toast.error({
        title: "Failed to delete page",
        description: String(error),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    createFile,
    renameFile,
    duplicateFile,
    deleteFile,
  };
}
